import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from "@angular/material/progress-bar";
import { BackgroundJobTrackerService } from "../../services/background-job-tracker.service";
import { NotificationCardComponent } from "../shared/notification-card/notification-card.component";
import { ChatContextService } from "../../../../agent-ui";

/**
 * Sticky panel that lists active background jobs for the current conversation.
 * Invisible when no jobs are tracked. Clicking a row scrolls to the
 * corresponding TaskListCardComponent in the conversation.
 */
@Component({
  selector: "app-background-job-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatProgressBar, NotificationCardComponent],
  templateUrl: "./background-job-panel.component.html",
  styleUrl: "./background-job-panel.component.scss",
})
export class BackgroundJobPanelComponent {
  protected readonly tracker = inject(BackgroundJobTrackerService);

  // Optional: absent in contexts not scoped under lib-chat-container (e.g. DebugMockPanelComponent),
  // where the tracker itself is mocked/scoped and no cross-thread leak can occur.
  private readonly chatContext = inject(ChatContextService, { optional: true });

  /**
   * BackgroundJobTrackerService is a root-scoped singleton keyed by job_id only, so
   * activeJobs() holds jobs from every conversation. Scope the panel to jobs started by
   * the current thread — a job with no job_thread_id was hydrated from a patched block
   * belonging to the currently parsed conversation, so it's always shown.
   */
  protected readonly visibleJobs = computed(() => {
    const threadId = this.chatContext?.threadId();
    if (!threadId) return this.tracker.activeJobs();
    return this.tracker.activeJobs().filter((snap) => !snap.block.job_thread_id || snap.block.job_thread_id === threadId);
  });

  protected scrollToJob(jobId: string): void {
    document.getElementById(`bg-job-${jobId}`)?.scrollIntoView({ behavior: "smooth" });
  }
}
